import React from 'react';

import { Link } from '~/lib/react-router-dom';

import { Box, Typography } from '@mui/material';
import { GridRenderCellParams } from '@mui/x-data-grid';

import CopyableField from '~/components/Common/CopyableField';
import DefinitionText from '~/components/Common/Elements/DefinitionText';
import DefinitionTitle from '~/components/Common/Elements/DefinitionTitle';
import { recordPromoVisit } from '~/components/Promo/PromoRecentVisits';
import { promoCodePath, promoIdPath } from '~/components/Promo/promoUrls';

import { formatDateWeekDayShortMonth } from '~/services/TimeService';

export default function descriptionRenderer(
  params: GridRenderCellParams<
    App.PromoData & { addedBy: App.UserSummary | null; updatedBy: App.UserSummary | null },
    string
  >,
) {
  return (
    <Box
      component="dl"
      sx={{
        display: 'grid',
        gridTemplateColumns: '120px 1fr',
        rowGap: 0.5,
        columnGap: 1,
      }}
      title={`Promo ID: ${params.row.id_promo_code}`}
    >
      <DefinitionTitle>
        Code name: <CopyableField value={params.row.code_name} component="span" label=" " />
      </DefinitionTitle>
      <DefinitionText>
        <Link
          to={promoCodePath(params.row.code_name)}
          target="_blank"
          rel="noreferrer"
          onClick={() => recordPromoVisit({ code: params.row.code_name, href: promoCodePath(params.row.code_name) })}
        >
          {params.row.code_name}
        </Link>
      </DefinitionText>
      <DefinitionTitle>Brand:</DefinitionTitle>
      <DefinitionText>{params.row.brand}</DefinitionText>
      <DefinitionTitle>Type:</DefinitionTitle>
      <DefinitionText>{params.row.promo_type === 'percentage' ? 'Percentage' : 'Fixed'}</DefinitionText>
      <DefinitionTitle>Created at:</DefinitionTitle>
      <DefinitionText>{formatDateWeekDayShortMonth(params.row.created_at)}</DefinitionText>
      <DefinitionTitle>Expires at:</DefinitionTitle>
      <DefinitionText>{formatDateWeekDayShortMonth(params.row.expires_at)}</DefinitionText>
      {params.row.expires_at && params.row.expires_at < new Date() && (
        <DefinitionText color="error">This promo has expired</DefinitionText>
      )}
      {params.row.added_by && (
        <>
          <DefinitionTitle>Created by: </DefinitionTitle>
          <DefinitionText>
            <CopyableField
              noWrap
              value={params.row.added_by}
              label={
                <Typography component="div">
                  <Link to={`/users/${params.row.added_by}`} target="_blank" rel="noreferrer">
                    {params.row.addedBy?.full_name ?? params.row.added_by}
                  </Link>
                </Typography>
              }
            />
          </DefinitionText>
        </>
      )}
      <DefinitionTitle>Updated at:</DefinitionTitle>
      <DefinitionText>{formatDateWeekDayShortMonth(params.row.updated_at)}</DefinitionText>
      {params.row.modified_by && (
        <>
          <DefinitionTitle>Updated by: </DefinitionTitle>
          <DefinitionText>
            <CopyableField
              noWrap
              value={params.row.modified_by}
              label={
                <Typography component="div">
                  <Link to={`/users/${params.row.modified_by}`} target="_blank" rel="noreferrer">
                    {params.row.modifiedBy?.full_name ?? params.row.modified_by}
                  </Link>
                </Typography>
              }
            />
          </DefinitionText>
        </>
      )}
      <DefinitionTitle>Description:</DefinitionTitle>
      <DefinitionText>{params.row.description}</DefinitionText>
      {params.row.cloned_from_promo_id && <DefinitionTitle>Child of:</DefinitionTitle>}
      {params.row.cloned_from_promo_id && (
        <DefinitionText>
          <Link to={promoIdPath(params.row.cloned_from_promo_id)} target="_blank" rel="noreferrer">
            <Typography title="The promo this promo was cloned from" color="primary" fontWeight="bold" noWrap>
              {params.row.cloned_from_promo_id}
            </Typography>
          </Link>
        </DefinitionText>
      )}
      <DefinitionTitle>Tags: </DefinitionTitle>
      <DefinitionText>
        {`${params.row.dept_tag} / ${params.row.category_tag} / ${params.row.sub_category_tag}`}
      </DefinitionText>
      <DefinitionTitle>
        promoId <CopyableField value={params.row.id_promo_code} label=" " />
      </DefinitionTitle>
      <DefinitionText>{params.row.id_promo_code}</DefinitionText>
    </Box>
  );
}
